import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { updateChequeEstado } from "../../../Api/ChequesApi";

/* =========================
   ESTADOS PERMITIDOS
========================= */
const estadosPermitidos = (estadoActual) => {
  switch (estadoActual) {
    case "IMPRESO":
      return ["IMPRESO", "ENTREGADO", "ANULADO"];
    case "ENTREGADO":
      return ["ANULADO"];
    default:
      return [];
  }
};

const ChequeEstadoModal = ({ show, onHide, cheque, onSaved }) => {
  const [estado, setEstado] = useState("");
  const [numeroderecibo, setNumeroDeRecibo] = useState("");
  const [motivodeanulado, setMotivoDeAnulado] = useState("");
  const [loading, setLoading] = useState(false);

  /* =========================
     INIT
  ========================= */
  useEffect(() => {
    if (cheque && show) {
      // 👉 Si viene ENTREGADO, se fuerza ANULADO
      if (cheque.estado === "ENTREGADO") {
        setEstado("ANULADO");
      } else {
        setEstado(cheque.estado);
      }

      setNumeroDeRecibo(cheque.numeroderecibo || "");
      setMotivoDeAnulado("");
    }
  }, [cheque, show]);

  const opcionesEstado = useMemo(
    () => (cheque ? estadosPermitidos(cheque.estado) : []),
    [cheque]
  );

  /* =========================
     GUARDAR
  ========================= */
  const guardar = async () => {
    // 🔴 Motivo obligatorio SIEMPRE que sea ANULADO
    if (estado === "ANULADO" && !motivodeanulado.trim()) {
      alert("Debe ingresar el motivo de la anulación");
      return;
    }

    setLoading(true);
    try {
      await updateChequeEstado(cheque.idcheque, {
        estado,
        numeroderecibo:
          estado === "ENTREGADO"
            ? numeroderecibo || null
            : cheque.numeroderecibo,
        motivodeanulado: estado === "ANULADO" ? motivodeanulado : null,
      });

      onSaved();
      onHide();
    } catch (err) {
      console.error(err);
      alert("Error al actualizar el estado del cheque");
    } finally {
      setLoading(false);
    }
  };

  if (!cheque) return null;

  const esCreado = cheque.estado === "CREADO";
  const esEntregado = cheque.estado === "ENTREGADO";
  const esAnulado = cheque.estado === "ANULADO";

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Cambiar estado del cheque</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {esCreado && (
          <Alert variant="info">
            El cheque está en estado <b>CREADO</b>.<br />
            No se permite modificar el estado.
          </Alert>
        )}

        {esEntregado && (
          <Alert variant="warning">
            El cheque fue <b>ENTREGADO</b>.<br />
            Debe <b>ANULARSE</b> indicando un motivo.
          </Alert>
        )}

        {esAnulado && (
          <Alert variant="danger">
            Este cheque ya está <b>ANULADO</b> y no puede modificarse.
          </Alert>
        )}

        {/* ESTADO */}
        {!esCreado && !esAnulado && cheque.estado !== "ENTREGADO" && (
          <Form.Group className="mb-3">
            <Form.Label>Estado</Form.Label>
            <Form.Select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              {opcionesEstado.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* NUMERO DE RECIBO */}
        {estado === "ENTREGADO" && (
          <Form.Group className="mb-3">
            <Form.Label>Número de recibo</Form.Label>
            <Form.Control
              value={numeroderecibo}
              onChange={(e) => setNumeroDeRecibo(e.target.value)}
              placeholder="Ingrese número de recibo"
            />
          </Form.Group>
        )}

        {/* MOTIVO DE ANULACIÓN (SIEMPRE OBLIGATORIO) */}
        {estado === "ANULADO" && !esAnulado && (
          <Form.Group className="mb-3">
            <Form.Label>Motivo de anulación *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={motivodeanulado}
              onChange={(e) => setMotivoDeAnulado(e.target.value)}
              placeholder="Indique el motivo de la anulación"
              required
              autoFocus
            />
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cerrar
        </Button>

        {!esCreado && !esAnulado && (
          <Button variant="danger" onClick={guardar} disabled={loading}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ChequeEstadoModal;
